<form
  [signalForm]="configForm"
  class="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 py-4"
>
  <section class="flex flex-col gap-3">
    <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-400">
      Configuration
    </h3>
    <lib-signal-text-field
      label="Label"
      [formField]="configForm.label"
    />
    <lib-signal-text-field
      label="Description"
      placeholder="Shown under the field"
      [formField]="configForm.description"
    />
  </section>
  <section class="flex flex-col gap-3">
    <div class="flex items-center justify-between gap-2">
      <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-400">
        Items (Rows)
      </h3>
      @if (!isCollectionLinked()) {
        <app-link-collection-button
          (collectionSelected)="onCollectionSelected($event)"
        />
      }
    </div>

    @if (linkedCollection(); as collection) {
      <div
        class="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2"
      >
        <app-svg-icon
          src="assets/icons/database-outline.svg"
          [size]="16"
        />

        <div class="min-w-0 flex-1">
          <p class="truncate text-sm font-medium text-slate-900">
            {{ collection.label || collection.key || 'Collection' }}
          </p>

          <p class="truncate text-xs text-slate-500">
            {{ collection.displayFieldLabel || collection.displayFieldKey }}
          </p>
        </div>

        <button
          type="button"
          class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-primary-600 hover:bg-primary-50"
          (click)="changeShowAsColumn()"
          aria-label="Change display field"
        >
          <app-svg-icon
            src="assets/icons/square-edit-outline.svg"
            [size]="16"
          />
        </button>

        <button
          type="button"
          class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
          (click)="detachLinkedCollection()"
          aria-label="Detach collection"
        >
          <app-svg-icon
            src="assets/icons/link-variant-off.svg"
            [size]="16"
          />
        </button>

        <button
          type="button"
          class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600"
          (click)="removeLinkedCollection()"
          aria-label="Remove collection"
        >
          <app-svg-icon class="cursor-pointer"
                        src="assets/icons/delete-outline.svg"
                        [size]="16"
          />
        </button>
      </div>
    } @else {
      <div
        class="flex flex-col gap-2"
        cdkDropList
        (cdkDropListDropped)="onItemDrop($event)"
      >
        @for (item of model().items; track $index; let i = $index) {
          <div class="flex items-center gap-2" cdkDrag>
            <button
              type="button"
              cdkDragHandle
              class="inline-flex h-8 w-8 shrink-0 cursor-grab items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 active:cursor-grabbing"
              aria-label="Reorder item"
            >
              <app-svg-icon
                src="assets/icons/drag-vertical.svg"
                [size]="18"
              />
            </button>

            <lib-signal-text-field
              class="min-w-0 flex-1"
              label=""
              size="sm"
              placeholder="Enter item name"
              [formField]="configForm.items[i].display"
            />
            <button
              type="button"
              class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 transition cursor-pointer hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40"
              [disabled]="model().items.length <= 1"
              (click)="removeItem(i)"
              aria-label="Delete item"
            >
              <app-svg-icon
                src="assets/icons/delete-outline.svg"
                [size]="16"
              />
            </button>
            <div
              *cdkDragPlaceholder
              class="h-10 rounded-lg border border-dashed border-slate-300 bg-slate-50"
            ></div>
          </div>
        }
      </div>

      <lib-signal-button
        type="button"
        appearance="outline"
        size="sm"
        [fullWidth]="true"
        iconSrc="assets/icons/plus.svg"
        (click)="addItem()"
      >
        Add item
      </lib-signal-button>
    }
  </section>

  <section class="flex flex-col gap-3">
    <div class="flex items-center justify-between gap-2">
      <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-400">
        Columns
      </h3>
      <span class="text-xs text-slate-400">
        {{ model().columns.length }} columns
      </span>
    </div>

    <div
      class="flex flex-col gap-2"
      cdkDropList
      (cdkDropListDropped)="onColumnDrop($event)"
    >
      @for (column of model().columns; track column.id; let i = $index) {
        <div class="flex items-center gap-2" cdkDrag>
          <button
            type="button"
            cdkDragHandle
            class="inline-flex h-8 w-8 shrink-0 cursor-grab items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 active:cursor-grabbing"
            aria-label="Reorder column"
          >
            <app-svg-icon
              src="assets/icons/drag-vertical.svg"
              [size]="18"
            />
          </button>
          <lib-signal-text-field
            class="min-w-0 flex-1"
            label=""
            size="sm"
            placeholder="Enter column name"
            [formField]="configForm.columns[i].label"
          />
          <button
            type="button"
            class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg cursor-pointer bg-slate-100 text-slate-500 transition hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40"
            [disabled]="model().columns.length <= 1"
            (click)="removeColumn(i)"
            aria-label="Delete column"
          >
            <app-svg-icon
              src="assets/icons/delete-outline.svg"
              [size]="16"
            />
          </button>
          <div
            *cdkDragPlaceholder
            class="h-10 rounded-lg border border-dashed border-slate-300 bg-slate-50"
          ></div>
        </div>
      }
    </div>

    <lib-signal-button
      type="button"
      appearance="outline"
      size="sm"
      [fullWidth]="true"
      iconSrc="assets/icons/plus.svg"
      (click)="addColumn()"
    >
      Add column
    </lib-signal-button>
  </section>

  <section class="flex flex-col gap-3">
    <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-400">
      Validators
    </h3>

    <lib-signal-checkbox
      label="Required"
      [formField]="configForm.required"
    />
  </section>
</form>
